/**
 * Invoice Configuration Types and Constants
 *
 * This file consolidates all invoice-related types, constants, and enums
 * that are used throughout the application for workflow management.
 */

// Re-export existing types from types/index.ts
export type { InvoiceStatus, InvoiceClassification } from '@types/index';

// Re-export constants from constants/invoiceStatus.ts
export { INVOICE_STATUSES, ASSIGNMENT_REQUIRED_STATUSES } from '@constants/invoiceStatus';

// Invoice Field Enums
export enum InvoiceField {
  SUPPLIER = 'supplier',
  INVOICE_NUMBER = 'invoice_number',
  INVOICE_DATE = 'invoice_date',
  DUE_DATE = 'due_date',
  TOTAL_AMOUNT = 'total_amount',
  SUPPLIER_ABN = 'supplier_abn',
  CLASSIFICATION = 'classification',
  STORE_ID = 'store_id',
  PO = 'po',
  SO = 'so',
  IR = 'ir',
  INV = 'inv',
  CLAIM = 'claim',
  CN = 'cn',
  CREDIT_AMOUNT = 'credit_amount',
  NOTES = 'notes',
  NEW_STOCK = 'new_stock',
  PRODUCT_ADDED = 'product_added'
}

// User Role Enum (separate from Role type for clarity)
export enum UserRole {
  ADMINISTRATOR = 'Administrator',
  FINANCE = 'Finance',
  STOCK = 'Stock'
}

// Invoice Status Enum (using the constants from invoiceStatus.ts)
export enum InvoiceStatus {
  UNPROCESSED = 'unprocessed',
  ADDING = 'adding',
  NEW_PRODUCTS = 'new_products',
  IR = 'ir',
  INVOICING = 'invoicing',
  HEAD_OFFICE_STOCK = 'head_office_stock',
  EXPENSE_INVOICES = 'expense_invoices',
  ENTERING_STOCK = 'entering_stock',
  ENTERING_EXPENSE = 'entering_expense',
  CREDIT_NOTE = 'credit_note',
  CREDIT_ENTERING = 'credit_entering',
  COMPLETED = 'completed'
}

// Invoice Classification Enum
export enum InvoiceClassification {
  DELIVER_DIRECT = 'deliver_direct',
  HEAD_OFFICE = 'head_office',
  EXPENSE = 'expense',
  CREDIT_NOTE = 'credit_note'
}

// Invoice Field Permissions
export enum InvoiceFieldPermission {
  HIDDEN = 'hidden',
  VIEW = 'view',
  EDIT = 'edit',
  REQUIRED = 'required'
}

// Workflow Actions
export enum WorkflowAction {
  ASSIGN = 'assign',
  MOVE_TO_NEXT = 'move_to_next',
  UPDATE_NOTES = 'update_notes',
  VIEW = 'view',
  EDIT = 'edit'
}

// Helper functions for type checking
export const isInvoiceStatus = (status: string): status is InvoiceStatus => {
  return Object.values(InvoiceStatus).includes(status as InvoiceStatus);
};

export const isInvoiceClassification = (classification: string): classification is InvoiceClassification => {
  return Object.values(InvoiceClassification).includes(classification as InvoiceClassification);
};

export const isUserRole = (role: string): role is UserRole => {
  return Object.values(UserRole).includes(role as UserRole);
};

export const isInvoiceField = (field: string): field is InvoiceField => {
  return Object.values(InvoiceField).includes(field as InvoiceField);
};

// Type guards for runtime validation
export const validateInvoiceStatus = (status: any): InvoiceStatus | null => {
  if (typeof status === 'string' && isInvoiceStatus(status)) {
    return status;
  }
  return null;
};

export const validateInvoiceClassification = (classification: any): InvoiceClassification | null => {
  if (typeof classification === 'string' && isInvoiceClassification(classification)) {
    return classification;
  }
  return null;
};

// Export all invoice statuses as a type-safe array
export const ALL_INVOICE_STATUSES = Object.values(InvoiceStatus) as readonly InvoiceStatus[];

// Export all invoice classifications as a type-safe array
export const ALL_INVOICE_CLASSIFICATIONS = Object.values(InvoiceClassification) as readonly InvoiceClassification[];

// Export all user roles as a type-safe array
export const ALL_USER_ROLES = Object.values(UserRole) as readonly UserRole[];

// Export all invoice fields as a type-safe array
export const ALL_INVOICE_FIELDS = Object.values(InvoiceField) as readonly InvoiceField[];